import { useRef, useState } from "react";
import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

interface AvatarUploadProps {
  avatarUrl: string | null;
  onUpload: (file: File) => void;
  loading: boolean;
}

export function AvatarUpload({ avatarUrl, onUpload, loading }: AvatarUploadProps) {
  const { locale } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  function handleClick() {
    inputRef.current?.click();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setSizeError(null);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSizeError(t(locale, "fileTooLarge"));
      // Reset input so same file can be re-selected after error
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // Generate local preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    onUpload(file);
  }

  const displayUrl = preview ?? avatarUrl;

  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative group">
        <div className="w-24 h-24 rounded-full border-4 border-primary/20 p-1 mb-4 overflow-hidden bg-muted flex items-center justify-center">
          {displayUrl ? (
            <img
              alt="Logo"
              className="w-full h-full object-cover rounded-full"
              src={displayUrl}
            />
          ) : (
            <span className="material-icons-outlined text-4xl text-muted-foreground">
              person
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleClick}
          disabled={loading}
          className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="material-icons-outlined text-white animate-spin">refresh</span>
          ) : (
            <span className="material-icons-outlined text-white">camera_alt</span>
          )}
        </button>
      </div>
      <h3 className="text-primary font-bold">{t(locale, "partnerLogo")}</h3>
      {sizeError && (
        <p className="text-red-500 text-xs mt-1 max-w-[160px]">{sizeError}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
